import React from 'react';
import { string, func, bool, number, shape, object } from 'prop-types';
import styled from 'styled-components';
import { translate } from '@airasia-common/libraries/i18n/LocaleProvider';
import {
  formatTime,
  parseAPIDay,
  SHORT_DATE_FORMAT,
} from '@airasia-common/libraries/datetime';
import { TextView, pd } from '@airasia-phoenix/web';
import Row from '../../Container/Row';
import { ToolTipIcon } from '../../../utils/statics';
import IconOverLap from '../OverLapIcons';

import {
  LineContainer,
  Container,
  DatePlus,
  Icon,
  Dot,
  DottedLine,
  MidText,
  FlightDetails,
  CardWrapper,
} from './TripSummary.Component';

export const color = pd.sys.colour;
export const font = pd.sys.typescale;

const TripSummary = ({
  origin,
  destination,
  departTime,
  arrivalTime,
  departDate,
  duration,
  days = '0',
  tripType,
  handleIconClick,
  airlineName,
  legs,
  stopsInfoKey,
  isShowStopsInfo,
  carrierLogo,
  carrierLogo1,
  carrierLogo2,
  lineStyle,
  summaryRowContentStyles,
}) => {
  const numberOfStops =
    legs !== null && legs !== undefined ? parseInt(legs) + 1 : '';

  const displayDepartDate = !isNaN(Date.parse(departDate))
    ? formatTime(parseAPIDay(departDate), SHORT_DATE_FORMAT)
    : '';
  const formatDuration = duration => {
    if (
      typeof duration === 'string' &&
      duration.includes(':') &&
      duration !== ''
    ) {
      const [hours, minutes] = duration.split(':').map(Number);
      const formattedDuration = [];
      formattedDuration.push(hours > 0 ? `${hours}h` : '');
      formattedDuration.push(minutes > 0 ? `${minutes}m` : '');
      return formattedDuration.join(' ');
    }
    return '';
  };

  const legsText =
    Number(legs) === 0
      ? translate('flightCard.direct')
      : translate('flightCard.legs', Number(legs));

  const stopsInformation = translate('flightCard.stopsLegs', {
    legs: legsText,
  });

  const metaText = formatDuration(duration);
  const layoverText =
    stopsInfoKey && isShowStopsInfo ? stopsInformation : legsText;
  const subText = `${displayDepartDate} | ${airlineName}`;
  const imageSrc = ToolTipIcon;

  return (
    <CardWrapper justifyContent="space-between" alignItems="center">
      <Row>
        <FlightDetails>
          <Row ml={'-2px'}>
            <TextView
              font={font.caption_text_medium}
              color={color.content_primary.color()}
              variant={'span'}
            >
              {tripType}
            </TextView>
            <TextView
              font={font.caption_text_medium}
              color={color.content_primary.color()}
              style={{ marginLeft: '4px' }}
              variant={'span'}
            >
              {subText}
            </TextView>
          </Row>
          <Row marginTop={2} {...summaryRowContentStyles}>
            <div>
              {numberOfStops > 0 && (
                <IconOverLap
                  className={'icon-overlap'}
                  stop={numberOfStops}
                  carrierLogo={carrierLogo}
                  carrierLogo1={carrierLogo1}
                  carrierLogo2={carrierLogo2}
                />
              )}
            </div>

            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                marginLeft: '10px',
              }}
            >
              <TextView
                data-testid="depart-time"
                font={pd.sys.typescale.body_text_medium}
                color={color.content_primary.color()}
                variant={'span'}
              >
                {departTime}
              </TextView>
              <TextView
                data-testid="origin"
                font={font.caption_text_medium}
                color={color.content_primary.color()}
                variant={'span'}
              >
                {origin}
              </TextView>
            </div>

            <Container>
              <TextView
                font={font.caption_text_small}
                color={color.content_secondary.color()}
                variant={'span'}
                style={{ marginRight: '14px', marginBottom: '2px' }}
              >
                {metaText}
              </TextView>
              <LineContainer {...lineStyle}>
                <Dot />
                <DottedLine />
                <MidText
                  font={font.caption_text_small}
                  color={color.interactive_link.color()}
                  variant={'span'}
                >
                  {layoverText}{' '}
                  <Icon
                    className={'tool-tip-icon'}
                    src={imageSrc}
                    onClick={handleIconClick}
                  />
                </MidText>
                <DottedLine />
                <Dot />
              </LineContainer>
            </Container>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
              }}
            >
              <TextView
                data-testid="arrival-time"
                font={pd.sys.typescale.body_text_medium}
                color={color.content_primary.color()}
                variant={'span'}
              >
                {arrivalTime}
              </TextView>
              <TextView
                data-testid="destination"
                font={font.caption_text_medium}
                color={color.content_primary.color()}
                variant={'span'}
                style={{ marginLeft: '22px' }}
              >
                {destination}
              </TextView>

              {Number(days) > 0 && (
                <DatePlus
                  data-testid="date-plus"
                  font={font.caption_text_small}
                  color={color.content_primary.color()}
                  variant={'span'}
                >
                  +{days}
                </DatePlus>
              )}
            </div>
          </Row>
        </FlightDetails>
      </Row>
    </CardWrapper>
  );
};

TripSummary.propTypes = {
  origin: string.isRequired,
  destination: string.isRequired,
  departTime: string.isRequired,
  arrivalTime: string.isRequired,
  departDate: string.isRequired,
  duration: string,
  days: string,
  legs: string.isRequired,
  isSnapOMSFlightsWeb: bool,
  airlineCode: string,
  stopsInfoKey: string,
  isShowStopsInfo: bool,
  tripType: string,
  handleIconClick: func,
  airlineName: string,
  airlineClassName: string,
  carrierLogo: string,
  carrierLogo1: string,
  carrierLogo2: string,
  summaryRowContentStyles: object,
  lineStyle: object,
};

TripSummary.defaultProps = {
  summaryRowContentStyles: {},
  lineStyle: {},
};

export default TripSummary;
